import type { SessionStatus } from '@zeus/index'
import { userDisplayName } from '~/helpers/users/display-name'
import { matchesQuery } from '~/utils/search-text'

export type SessionFilters = {
  query: string
  /** Vide pour tous. */
  status: SessionStatus | ''
  /** Id d'un module du parcours, ou vide pour tous. */
  moduleId: string
  /** Membres du groupe filtré (#29), ou rien pour tous. */
  groupMemberIds?: ReadonlySet<string> | null
  /** Id de l'étiquette filtrée (#29), ou vide pour toutes. */
  tagId?: string
  /** Id du formateur filtré (#244), ou vide pour tous. */
  trainerId?: string
}

type PathModule = { id: string, title: string }

type FilterableSession = {
  name: string
  status: SessionStatus
  modules?: ReadonlyArray<{ module?: PathModule | null }> | null
  enrolledUsers?: ReadonlyArray<{ id: string }> | null
  tags?: ReadonlyArray<SessionTag> | null
  trainers?: ReadonlyArray<SessionTrainer> | null
}

type SessionTag = { id: string, name: string }
type SessionTrainer = { id: string, firstName: string, lastName: string, email?: string | null }
/** Une option de filtre : un id, et le libellé du menu. */
type FilterOption = { id: string, name: string }

/** Au moins un inscrit dans le groupe (#29) : le groupe « est » dans le parcours dès qu'il y a quelqu'un. */
function reachesGroup(session: FilterableSession, members: ReadonlySet<string> | null | undefined): boolean {
  return !members || (session.enrolledUsers ?? []).some(user => members.has(user.id))
}

function pathModules(session: FilterableSession): PathModule[] {
  return (session.modules ?? []).flatMap(step => (step.module ? [step.module] : []))
}

/**
 * Sessions qui répondent à la recherche — sur leur nom ou le titre de
 * **n'importe quel** module du parcours, pas seulement le premier que montre la
 * liste —, au statut et au module demandés (#107), et au groupe (#29).
 */
export function filterSessions<T extends FilterableSession>(sessions: readonly T[], filters: SessionFilters): T[] {
  return sessions.filter((session) => {
    const modules = pathModules(session)
    return (!filters.status || session.status === filters.status)
      && (!filters.moduleId || modules.some(module => module.id === filters.moduleId))
      && reachesGroup(session, filters.groupMemberIds)
      && (!filters.tagId || (session.tags ?? []).some(tag => tag.id === filters.tagId))
      && (!filters.trainerId || (session.trainers ?? []).some(trainer => trainer.id === filters.trainerId))
      && matchesQuery(filters.query, [session.name, ...modules.map(module => module.title)])
  })
}

/**
 * Options du filtre « module » : ceux qu'utilisent les sessions chargées, une
 * fois chacun, triés par titre. Un module qu'aucune session n'utilise n'y figure
 * pas — il ne pourrait que vider la liste.
 */
export function sessionModuleOptions(sessions: readonly FilterableSession[]): PathModule[] {
  const byId = new Map(sessions.flatMap(pathModules).map(module => [module.id, module]))
  return [...byId.values()]
    .map(({ id, title }) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * Le module filtré, s'il figure encore parmi les options ; sinon aucun. Les
 * options suivent les sessions chargées : supprimer la dernière session d'un
 * module le retire du menu, qui affiche alors « Tous les modules » — le filtre
 * ne doit pas continuer à vider la liste en coulisse.
 */
export function activeModuleFilter(moduleId: string, options: readonly PathModule[]): string {
  return options.some(option => option.id === moduleId) ? moduleId : ''
}

/**
 * Options du filtre « étiquette » (#29) : celles que portent les parcours
 * chargés, une fois chacune, par ordre alphabétique. Comme pour les modules,
 * une étiquette qu'aucun parcours ne porte ne pourrait que vider la liste.
 */
export function sessionTagOptions(sessions: readonly FilterableSession[]): SessionTag[] {
  const byId = new Map(sessions.flatMap(session => session.tags ?? []).map(tag => [tag.id, tag]))
  return [...byId.values()]
    .map(({ id, name }) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Options du filtre « formateur » (#244) : ceux qui animent au moins un des
 * parcours chargés, une fois chacun, par nom. Même règle que les étiquettes.
 */
export function sessionTrainerOptions(sessions: readonly FilterableSession[]): FilterOption[] {
  const byId = new Map(sessions.flatMap(session => session.trainers ?? []).map(trainer => [trainer.id, trainer]))
  return [...byId.values()]
    .map(trainer => ({ id: trainer.id, name: userDisplayName(trainer) }))
    .sort((a, b) => a.name.localeCompare(b.name))
}
